// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useMemo } from 'react';
import {
  Alert, AlertTitle, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, Grid, InputLabel, MenuItem,
  Select, TextField, Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../utils/api';
import { dateTime } from '../../utils/dates';
import { errorMessage } from '../../utils/erreurs';
import { saveFile } from '../../utils/fichiers';
import { CODE, effectiveStatus } from './commun';

// Écrite dans chaque fichier qui appelle : `backend/tests/test_console_routes.py`
// ne résout une constante de chemin que dans le fichier qui la déclare.
const ORDERS_ROUTE = '/cles-de-pose';

// Schéma du document de fabrication, seul accepté par l'atelier
// (`edge/site-server-tools/cle-de-pose/fabriquer-depuis-ordre.sh`). Vérifié ici
// pour qu'un écart entre console et backend soit signalé sur l'écran qui crée
// l'ordre, et non devant la clé.
const ORDER_SCHEMA = 'vigeos.ordre-de-fabrication/1';

/**
 * Préparation d'une clé : choix de l'établissement, création de l'ordre, remise
 * du fichier et consigne de fabrication.
 *
 * `dialogue` est l'état tenu par la page, null quand la fenêtre est fermée ;
 * `document` y porte l'ordre rendu par la création, secret compris. Il ne vit
 * que dans ce dialogue et disparaît à la fermeture : ni la liste ni le stockage
 * local n'en gardent de copie. `onUpdate` complète cet état, `onCree` recharge la
 * liste après la création, `onDelivered` ferme une fois le fichier remis.
 *
 * Le dialogue ne se ferme ni au clic hors cadre ni à Échap une fois l'ordre
 * créé : c'est la seule fenêtre depuis laquelle le fichier peut encore être
 * réécrit si le navigateur a bloqué le téléchargement automatique.
 */
export default function DialoguePreparation({
  dialogue, sites, erreurSites: sitesError, ordres: installOrders, onMaj: onUpdate, onFermer: onClose, onCree, onRemis: onDelivered,
}) {
  /**
   * Écrit le fichier depuis la mémoire de l'onglet. La réponse de création
   * porte le document et le nom sous lequel l'écrire ; seul le sous-objet
   * `order` est sérialisé, car l'atelier refuse tout ce qui n'a pas `schema` à
   * la racine. Attention : cette re-sérialisation change les octets, elle ne
   * conviendrait donc pas à un ordre signé.
   */
  const writeFile = (text, fileName) => {
    // Révocation immédiate, le défaut : la copie que le navigateur garde pour
    // cette URL porte le secret.
    saveFile(new Blob([text], { type: 'application/json' }), fileName);
  };

  const createOrder = async () => {
    if (!dialogue?.siteId) return;
    onUpdate({ enCours: true, erreur: '' });
    try {
      // Tout passe dans le corps : ni le site, ni le repère, ni le secret en
      // retour ne transitent par le chemin, que nginx journalise
      // (`log_format main` inclut `$request`) et que Cloudflare voit.
      const res = await api.post(ORDERS_ROUTE, {
        site_id: dialogue.siteId,
        notes: dialogue.repere.trim() || null,
      });

      // La réponse est `{file, order}` : le nom à écrire et le document.
      const orderDoc = res.data?.ordre;
      if (!orderDoc || typeof orderDoc !== 'object') {
        onUpdate({ enCours: false, erreur: "La réponse du serveur ne porte pas d'ordre de fabrication. Rien n'a été téléchargé." });
        return;
      }
      // Même contrôle que l'atelier, mais expliqué ici : une clé gravée sur un
      // schéma inconnu poserait une identité fausse sur un serveur destiné à un
      // client.
      if (orderDoc.schema !== ORDER_SCHEMA) {
        onUpdate({
          enCours: false,
          erreur: `L'ordre rendu annonce le schéma « ${orderDoc.schema || 'aucun'} », `
            + `alors que l'atelier ne sait lire que « ${ORDER_SCHEMA} ». Le fichier `
            + "n'a pas été téléchargé : mettez la console et le poste de gravure "
            + 'au même niveau avant de graver.',
        });
        return;
      }

      const fileName = res.data.fichier || `ordre-${orderDoc.serie || 'sans-serie'}.vigeos.json`;
      const text = JSON.stringify(orderDoc, null, 2);
      writeFile(text, fileName);
      onUpdate({
        enCours: false,
        document: orderDoc,
        texteBrut: text,
        nomFichier: fileName,
      });

      onCree();
    } catch (e) {
      onUpdate({ enCours: false, erreur: errorMessage(e, "Échec de la préparation de la clé") });
    }
  };

  const selectedSite = dialogue ? sites.find((s) => s.id === dialogue.siteId) : null;
  const doc = dialogue?.document || null;

  // Ce que l'établissement choisi a déjà. Un site peut porter plusieurs
  // serveurs, un par bâtiment ou par aile (le backend route chaque chambre vers
  // le serveur dont une caméra lui est assignée,
  // `provision._config_payload_site`) : une deuxième clé n'est pas interdite,
  // mais l'existant est montré pour éviter un serveur de trop.
  const siteOrders = useMemo(
    () => (dialogue?.siteId ? installOrders.filter((o) => o.siteId === dialogue.siteId) : []),
    [installOrders, dialogue],
  );
  const siteInstallations = siteOrders.filter((o) => effectiveStatus(o) === 'pose_confirmee').length;
  const pendingForSite = siteOrders.filter((o) => effectiveStatus(o) === 'emis').length;

  return (
    <Dialog
      open={!!dialogue}
      onClose={(e, reason) => {
        if (doc && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
        onClose();
      }}
      disableEscapeKeyDown={!!doc}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {doc ? `Clé ${doc.serie} : consigne de fabrication` : 'Préparer une clé de pose'}
      </DialogTitle>
      <DialogContent dividers>
        {dialogue && !doc && (
          <>
            {dialogue.erreur && <Alert severity="error" sx={{ mb: 2 }}>{dialogue.erreur}</Alert>}
            {sites.length === 0 ? (
              <Alert severity={sitesError ? 'error' : 'warning'}>
                {sitesError ? (
                  <>
                    <AlertTitle>Liste des établissements illisible</AlertTitle>
                    {sitesError}
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Ce n'est pas « aucun établissement » : la liste n'a pas pu être
                      lue. Si c'est un refus, il manque à ce compte le droit sur les
                      clients.
                    </Typography>
                  </>
                ) : (
                  <>
                    Aucun établissement en service n'est disponible. Ouvrez-en un
                    depuis l'écran Établissements avant de préparer une clé.
                  </>
                )}
              </Alert>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Ce geste crée le serveur de l'établissement et son secret. Le
                  secret ne sera rendu qu'une fois, dans le fichier téléchargé,
                  et ne sera jamais réaffiché ni relu par aucune route.
                </Alert>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Établissement</InputLabel>
                      <Select
                        value={dialogue.siteId}
                        label="Établissement"
                        onChange={(e) => onUpdate({ siteId: e.target.value, erreur: '' })}
                      >
                        {sites.map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.organisation} · {s.nom}{s.ville ? ` (${s.ville})` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small" label="Repère du serveur (facultatif)"
                      placeholder="Bâtiment A, rez-de-chaussée"
                      value={dialogue.repere}
                      inputProps={{ maxLength: 500 }}
                      onChange={(e) => onUpdate({ repere: e.target.value, erreur: '' })}
                      helperText="Utile dès qu'un établissement a plus d'un serveur : c'est ce qui distingue les deux lignes de la liste."
                    />
                  </Grid>
                </Grid>

                {selectedSite && selectedSite.chambres > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {selectedSite.chambres} chambre{selectedSite.chambres > 1 ? 's' : ''} déclarée
                    {selectedSite.chambres > 1 ? 's' : ''} sur cet établissement : c'est ce nombre
                    que l'ordre portera, compté à l'émission. Il n'y a rien à saisir.
                  </Typography>
                )}

                {/* Le comptage peut rendre zéro : l'ordre se grave et la pose
                    réussit, mais le serveur reçoit `rooms: []` et ne surveille
                    rien. L'avertissement vient avant la gravure. */}
                {selectedSite && selectedSite.chambres === 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <AlertTitle>Cet établissement ne déclare aucune chambre</AlertTitle>
                    La clé se gravera et la pose réussira, mais le serveur ne recevra
                    aucune chambre à surveiller tant qu'elles ne sont pas déclarées.
                    Il les prendra à son premier bonjour suivant, sans regraver.
                  </Alert>
                )}

                {siteOrders.length > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Cet établissement a déjà {siteOrders.length} clé
                    {siteOrders.length > 1 ? 's' : ''} préparée{siteOrders.length > 1 ? 's' : ''}
                    {siteInstallations > 0 && ` (${siteInstallations} pose${siteInstallations > 1 ? 's' : ''} confirmée${siteInstallations > 1 ? 's' : ''}`}
                    {siteInstallations > 0 && pendingForSite > 0 && `, ${pendingForSite} en attente`}
                    {siteInstallations > 0 && ')'}
                    {siteInstallations === 0 && pendingForSite > 0 && ` (${pendingForSite} en attente de pose)`}.
                    {' '}En préparer une autre est prévu : un site peut porter plusieurs serveurs,
                    un par bâtiment ou par aile. Chacun ne servira que les chambres dont une
                    caméra lui est assignée.
                  </Alert>
                )}
              </>
            )}
          </>
        )}

        {doc && (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Ce fichier porte l'identité du site</AlertTitle>
              <strong>{dialogue.nomFichier}</strong> vaut une fiche de coffre, pas
              une pièce jointe. Il ne passe ni par la messagerie, ni par un espace
              partagé, ni par une conversation. Il se dépose dans le dossier de
              gravure, et s'efface une fois la clé vérifiée.
            </Alert>

            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Établissement</Typography>
                <Typography variant="body2">
                  {doc.site?.nom || selectedSite?.nom || '—'}
                  {doc.site?.organisation ? ` · ${doc.site.organisation}` : ''}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Série</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                  {doc.serie}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Chambres attendues</Typography>
                <Typography variant="body2">{doc.chambres_attendues ?? '—'}</Typography>
              </Grid>
            </Grid>

            {/* Bleu, et non ambre ou rouge : dans cette fenêtre, ce qui doit
                ressortir est le secret du fichier. L'échéance est une
                contrainte de planning ; le rouge reste aux ordres réellement
                en défaut dans la liste. */}
            <Alert severity="info" sx={{ mb: 2 }}>
              Il expire le <strong>{dateTime(doc.expire_le, '—')}</strong>, soit 48 h
              après son émission. Passé ce délai la clé n'est plus gravable et il
              faut préparer un nouvel ordre.
            </Alert>

            <Divider sx={{ my: 2 }} />
            {/* Chemins écrits en toutes lettres, tels que le poste d'atelier
                les trouve : l'opérateur sait quoi faire du JSON sans autre
                document. */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Que faire de ce fichier</Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              <ol style={{ margin: 0, paddingLeft: '1.2rem' }}>
                <li>Brancher la clé USB à graver. Son contenu sera effacé.</li>
                <li>
                  Double-cliquer <Box component="code" sx={CODE}>ops\cle-de-pose\graveur\graveur.cmd</Box>.
                </li>
                <li>
                  Dans la fenêtre : choisir ce fichier, choisir la clé, cliquer
                  <strong> Graver</strong>. Le reste est automatique.
                </li>
                <li>
                  La clé est gravée puis relue, et l'ordre est <strong>effacé</strong> à
                  ce moment-là. Rien à nettoyer à la main.
                </li>
              </ol>
            </Typography>

            <Alert severity="info" sx={{ mt: 2 }}>
              <AlertTitle>Le poste d'atelier se prépare une fois</AlertTitle>
              La gravure a besoin, sur la machine, de l'image du pipeline, du
              modèle, de l'ISO Ubuntu et de la graine qui signe les charges. Rien
              de tout cela ne vient de la console. Marche à suivre :
              {' '}<Box component="code" sx={CODE}>edge/site-server-tools/cle-de-pose/README.md</Box>, section 4.
              Tant que ce n'est pas fait, le .cmd refuse et nomme ce qui manque.
            </Alert>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              Le secret du serveur n'est volontairement pas affiché ici : il n'a
              jamais à être lu ni retapé, et un écran est un endroit où il
              resterait (capture, partage de fenêtre, inspecteur du navigateur).
              Il n'existe que dans le fichier. Perdu, il ne se retrouve pas :
              il faut préparer une nouvelle clé.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {doc ? (
          <>
            {/* Réécrit le texte déjà en mémoire, si le navigateur a bloqué le
                téléchargement automatique. Rien n'est redemandé à l'API, et
                le bouton disparaît avec le dialogue. */}
            <Button startIcon={<DownloadIcon />} onClick={() => writeFile(dialogue.texteBrut, dialogue.nomFichier)}>
              Télécharger à nouveau le fichier
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" onClick={onDelivered}>
              J'ai le fichier, fermer
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={dialogue?.enCours}>Annuler</Button>
            <Button
              variant="contained"
              onClick={createOrder}
              disabled={!dialogue || dialogue.enCours || !dialogue.siteId}
            >
              {dialogue?.enCours ? <CircularProgress size={18} /> : 'Créer la clé et télécharger'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
